import { useEffect, useMemo, useState } from "react";
import { slug } from "./format";
import { useActiveHeading } from "./useActiveHeading";
import { useNightPaper, useReaderFont } from "./useNightPaper";
import { readingProgressPercent, useReadingProgress } from "./useReadingProgress";

export type ReaderChrome = ReturnType<typeof useReaderChrome>;

// Общий каркас ридеров: прогресс (только растёт), шкала шрифта, ночная бумага,
// список глав и активная глава. Использовать в Reader.tsx и PublicReader.tsx —
// поведение обязано совпадать, поэтому расхождения ловим только на уровне
// разных props в этих экранах, а не в двух копиях эффекта.
export function useReaderChrome(content: string, readingKey: string) {
  const [fontScale, setFontScale] = useReaderFont();
  const [progress, setProgress] = useState(() => readingProgressPercent(readingKey));
  const [night, setNight] = useNightPaper();
  const headings = useMemo(() => [...(content || "").matchAll(/^##\s+(.+)$/gm)].map((match) => match[1]), [content]);
  const headingIds = useMemo(() => headings.map(slug), [headings]);
  const activeHeading = useActiveHeading(headingIds);
  const { resumedAt, forgetResume } = useReadingProgress(readingKey);
  useEffect(() => {
    setProgress(readingProgressPercent(readingKey));
    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const next = max > 0 ? window.scrollY / max * 100 : 0;
      setProgress((current) => Math.max(current, next));
    };
    window.addEventListener("scroll", update, { passive: true });
    update();
    return () => window.removeEventListener("scroll", update);
  }, [readingKey]);
  return { fontScale, setFontScale, progress, night, setNight, headings, activeHeading, resumedAt, forgetResume };
}
